import { reactive, type App, type Plugin, type ComponentCustomProperties } from 'vue'
import SimpleAlert from './SimpleAlert.vue'

export type Level = 'info' | 'warn' | 'error'

// store
export interface AlertStore {
  message: string
  description?: string
  level: Level
  show: boolean
  button: boolean
  timeout: number
  maxWidth: string | number
  timeoutObj: number | undefined // 메시지 표시 텀, 여러개 동시 발생시 가장 나중의 메시지 표시를 위함.
}
const alertStore = reactive<AlertStore>({
  message: '',
  description: '',
  level: 'info',
  show: false,
  button: true,
  timeout: 0,
  maxWidth: 200,
  timeoutObj: undefined,
})

// options
interface Options {
  maxWidth?: string | number
  timeout?: number
  button?: boolean
}
const options = reactive<Options>({
  maxWidth: 500,
  timeout: 5000,
  button: true,
})

let dismissTimer: any

// call
interface Call {
  (message: string, level: Level, description?: string): void
}
const call: Call = (message, level, description) => {
  alertStore.show = false
  if (dismissTimer) clearTimeout(dismissTimer)
  if (alertStore.timeoutObj) clearTimeout(alertStore.timeoutObj)
  alertStore.timeoutObj = setTimeout(() => {
    alertStore.message = message
    alertStore.level = level
    alertStore.description = description || ''
    alertStore.show = true
    // 자동 닫기 타이머 설정 (options.timeout 기준)
    if (options.timeout && options.timeout > 0) {
      dismissTimer = setTimeout(() => {
        alertStore.show = false
      }, options.timeout)
    }
  }, 100)
}

// close
interface Close {
  (): void
}
const close: Close = () => {
  alertStore.show = false
  if (dismissTimer) clearTimeout(dismissTimer)
  if (alertStore.timeoutObj) clearTimeout(alertStore.timeoutObj)
}

// alert object
export interface Alert {
  info: (message: string, description?: string) => void
  success: (message: string, description?: string) => void
  warn: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  close: () => void
}

export const $alert: Alert = {
  info: (message, description) => call(message, 'info', description),
  success: (message, description) => call(message, 'info', description),
  warn: (message, description) => call(message, 'warn', description),
  error: (message, description) => call(message, 'error', description),
  close: () => close(),
}

const SimpleAlertPlugin: Plugin = {
  install(app: App, userPptions?: Options) {
    Object.assign(options, userPptions)
    if (typeof options.button !== 'undefined') alertStore.button = options.button
    if (typeof options.timeout !== 'undefined') alertStore.timeout = options.timeout
    if (typeof options.maxWidth !== 'undefined') alertStore.maxWidth = options.maxWidth

    app.config.globalProperties.$alert = $alert

    app.provide('alert', $alert)
    app.provide('alertStore', alertStore)

    app.component('SimpleAlert', SimpleAlert)
  },
}

export default SimpleAlertPlugin as Plugin<Options>

// --------------------------------------------------------------
// intellisense 를 위한 설정
// --------------------------------------------------------------
// Options API
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $alert: Alert
  }
}
// Composition API - provide/inject를 위한 전역 키 정의
declare module 'vue' {
  export function inject(key: 'alert'): Alert
  export function inject(key: 'alertStore'): AlertStore
}
